import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { ChatThread, ChatTurn, Conversation } from 'src/app/models/chat';
import { AIService } from '../ai/ai.service';
import { ChatMarkdownService } from '../chat-markdown/chat-markdown.service';
import { MixpanelService } from '../mixpanel/mixpanel.service';
import { ServiceBase } from '../service-base';

const CONNECTION_ERROR = 'Sorry, I am having trouble connecting to the server. Please try again later.';

/** An id for a conversation started here. The API takes a UUID for a thread it has not seen. */
export function newThreadId(): string {
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // randomUUID is only there on pages served securely
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The user's conversations with GPS AI: the list of them, and each one that has been opened.
 *
 * Kept here and not in the chat page so that an answer goes on arriving while the user reads
 * another conversation, or another page.
 */
@Injectable({
    providedIn: 'root',
})
export class ChatStateService implements ServiceBase {
    private _threads$ = new BehaviorSubject<ChatThread[]>([]);
    private conversations = new Map<string, Conversation>();
    private answers = new Subscription();
    private threadsRequested = false;
    /** Where the next page of the list follows on from, when there is one */
    private nextCursor: string | null = null;
    /** Deleted, and renamed, since the list was last asked for: its answer is older than these */
    private deletedSince = new Set<string>();
    private renamedSince = new Set<string>();
    private suggested$: Observable<string[]> | null = null;

    /** The conversations to list, the one last added to first */
    threads$ = this._threads$.asObservable();
    loadingThreads = false;
    /** The list could not be fetched. Asking for it again tries again. */
    threadsFailed = false;

    constructor(
        private aiService: AIService,
        private markdown: ChatMarkdownService,
        private mixpanelService: MixpanelService,
    ) { }

    get hasMoreThreads(): boolean {
        return this.nextCursor !== null;
    }

    /** Fetch the list of conversations, unless that has been done. */
    loadThreads(): void {
        if (!this.threadsRequested) {
            this.threadsRequested = true;
            this.fetchThreads();
        }
    }

    loadMoreThreads(): void {
        if (this.nextCursor !== null && !this.loadingThreads) {
            this.fetchThreads(this.nextCursor);
        }
    }

    /**
     * The questions to suggest for a new chat. The API picks them afresh each time it is asked,
     * so they are asked for once, and every new chat of the session suggests the same ones.
     */
    suggestedQuestions(): Observable<string[]> {
        if (!this.suggested$) {
            const suggested$: Observable<string[]> = this.aiService.getRecommendations().pipe(
                catchError(() => {
                    // A new chat does without them, and the next one asks again
                    if (this.suggested$ === suggested$) {
                        this.suggested$ = null;
                    }
                    return of([]);
                }),
                shareReplay(1),
            );
            this.suggested$ = suggested$;
        }
        return this.suggested$;
    }

    /** A conversation: the one in progress here, or else as it was saved. */
    open(threadId: string): Observable<Conversation> {
        const known = this.conversations.get(threadId);
        if (known) {
            return of(known);
        }
        return this.aiService.getThread(threadId).pipe(
            map(detail => {
                // Asked in while it was being fetched
                const started = this.conversations.get(threadId);
                if (started) {
                    return started;
                }
                const conversation: Conversation = {
                    threadId,
                    title: detail.title,
                    answering: false,
                    saved: true,
                    turns: detail.messages.map(message => ({
                        question: message.question,
                        markdown: message.response,
                        html: this.markdown.render(message.response),
                        waiting: false,
                        complete: true,
                    })),
                };
                this.conversations.set(threadId, conversation);
                return conversation;
            }),
        );
    }

    /**
     * Ask a question, in a conversation that is open or, given none, in a new one.
     *
     * @returns the id of the conversation, or null if there was nothing to ask
     */
    ask(prompt: string, threadId?: string): string | null {
        const question = prompt.trim();
        if (!question) {
            return null;
        }
        let conversation = threadId ? this.conversations.get(threadId) : undefined;
        if (!conversation) {
            // Called by its first question until the API, which titles it, is next asked
            const title = question.split(/\s+/).join(' ');
            conversation = { threadId: threadId ?? newThreadId(), title, turns: [], answering: false, saved: false };
            this.conversations.set(conversation.threadId, conversation);
        }
        if (conversation.answering) {
            return conversation.threadId;
        }
        const turn: ChatTurn = { question, markdown: '', html: '', waiting: true, complete: false };
        conversation.turns.push(turn);
        conversation.answering = true;
        this.bringToTop(conversation);

        const asked = conversation;
        const finish = () => {
            turn.waiting = false;
            turn.complete = true;
            asked.answering = false;
        };
        this.answers.add(this.aiService.getResponse(question, asked.threadId).subscribe({
            next: response => {
                if (response) {
                    // The API has the conversation once it starts to answer
                    asked.saved = true;
                    turn.waiting = false;
                    turn.markdown = response;
                    turn.html = this.markdown.render(response);
                }
            },
            error: error => {
                console.error('Error:', error);
                turn.html = CONNECTION_ERROR;
                turn.failed = true;
                finish();
                if (!asked.saved) {
                    // Not one to go back to: listed, it would be a link to nothing
                    this.unlist(asked.threadId);
                }
            },
            complete: () => {
                asked.saved = true;
                finish();
                this.mixpanelService.track('AI Chat Request');
            },
        }));
        return asked.threadId;
    }

    renameThread(threadId: string, title: string): Observable<void> {
        return this.aiService.renameThread(threadId, title).pipe(
            tap(renamed => {
                this.renamedSince.add(threadId);
                this.retitle(threadId, renamed.title);
            }),
            map(() => undefined),
        );
    }

    deleteThread(threadId: string): Observable<void> {
        return this.aiService.deleteThread(threadId).pipe(
            tap(() => {
                this.deletedSince.add(threadId);
                this.conversations.delete(threadId);
                this.unlist(threadId);
            }),
        );
    }

    clearCache(): void {
        // Answers still arriving are for whoever was signed in
        this.answers.unsubscribe();
        this.answers = new Subscription();
        this.conversations.clear();
        this._threads$.next([]);
        this.threadsRequested = false;
        this.loadingThreads = false;
        this.threadsFailed = false;
        this.nextCursor = null;
        this.deletedSince.clear();
        this.renamedSince.clear();
        this.suggested$ = null;
    }

    private fetchThreads(cursor?: string): void {
        this.loadingThreads = true;
        this.threadsFailed = false;
        this.deletedSince.clear();
        this.renamedSince.clear();
        this.answers.add(this.aiService.listThreads(cursor).subscribe({
            next: response => {
                // A conversation deleted while this was on its way is not to come back
                const results = response.results.filter(result => !this.deletedSince.has(result.thread_id));
                // Conversations started here since are already listed, and are newer. The API
                // has the last word on what they are called, unless they were renamed meanwhile.
                const listed = new Set(this._threads$.value.map(thread => thread.thread_id));
                for (const thread of results) {
                    if (listed.has(thread.thread_id) && !this.renamedSince.has(thread.thread_id)) {
                        this.retitle(thread.thread_id, thread.title);
                    }
                }
                this._threads$.next([
                    ...this._threads$.value,
                    ...results.filter(thread => !listed.has(thread.thread_id)),
                ]);
                this.nextCursor = response.next ? new URL(response.next).searchParams.get('cursor') : null;
                this.loadingThreads = false;
            },
            error: () => {
                this.loadingThreads = false;
                if (!cursor) {
                    // Nothing to show: say so, and let the next request for the list try again
                    this.threadsRequested = false;
                    this.threadsFailed = true;
                }
            },
        }));
    }

    /** List a conversation first, as the one last added to. */
    private bringToTop(conversation: Conversation): void {
        this._threads$.next([
            { thread_id: conversation.threadId, title: conversation.title, last_message_at: new Date().toISOString() },
            ...this._threads$.value.filter(thread => thread.thread_id !== conversation.threadId),
        ]);
    }

    private retitle(threadId: string, title: string): void {
        const conversation = this.conversations.get(threadId);
        if (conversation) {
            conversation.title = title;
        }
        this._threads$.next(this._threads$.value.map(
            thread => thread.thread_id === threadId ? { ...thread, title } : thread));
    }

    private unlist(threadId: string): void {
        this._threads$.next(this._threads$.value.filter(thread => thread.thread_id !== threadId));
    }
}
